import { useCallback, useEffect, useState } from 'react';

interface Size {
  width: number;
  height: number;
}

export default function useElementSize<T extends HTMLElement>() {
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });

  const ref = useElementSizeCallback<T>(setSize);

  return [ref, size] as const;
}

export function useElementSizeCallback<T extends HTMLElement>(
  onSizeUpdate: (newSize: Size) => void,
) {
  const [element, setElement] = useState<T | null>(null);

  // Use a ref function instead of a ref object as recommended by react docs:
  // https://reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
  const ref = useCallback(
    (newElement: T | null) => {
      setElement(newElement);
      const newSize = getElementSize(newElement);
      onSizeUpdate?.(newSize);
    },
    [onSizeUpdate],
  );

  const updateSize = useCallback(() => {
    const newSize = getElementSize(element);
    onSizeUpdate?.(newSize);
  }, [element, onSizeUpdate]);

  useEffect(() => {
    if (!element) {
      return;
    }

    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(element, { box: 'border-box' });
    return () => resizeObserver.disconnect();
  }, [element, updateSize]);

  return ref;
}

function getElementSize(element: HTMLElement | null) {
  if (!element) {
    return { width: 0, height: 0 };
  }
  return { width: element.offsetWidth, height: element.offsetHeight };
}
